/**
 * Main Layout Component
 * Provides navigation and layout structure
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Folder as FolderIcon,
  History as HistoryIcon,
  Inventory as PartsIcon,
  School as TrainingIcon,
  People as TeamsIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  Calculate as EstimatorIcon,
} from '@mui/icons-material';
import { useAppTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { currentTheme } = useAppTheme();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const isActive = (path: string) => location.pathname === path;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };
  
  // Get icon color based on theme
  const getIconColor = (isActive: boolean) => {
    if (isActive) return 'white';
    // PP C&A theme uses red icons, Indigo theme uses the primary color
    return currentTheme === 'ppcanda' ? '#d62839' : theme.palette.primary.main;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="static" 
        elevation={1}
        sx={{ 
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        {/* First Row: Logo, Centered Title, and powered by Kamna Ventures */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          py: 1.5,
          borderBottom: '1px solid #e0e0e0',
          px: 2
        }}>
          {/* PPCanda Logo - Left side */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3, minWidth: { xs: 100, md: 160 } }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="PPCANDA"
              sx={{ height: { xs: 50, md: 60 } }}
            />
          </Box>

          {/* Centered Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, justifyContent: 'center' }}>
            <Typography 
              variant="h4" 
              component="div"
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '2rem' },
                color: '#212529',
                lineHeight: 1.2,
              }}
            >
              Design Workflow Automation
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: { xs: 100, md: 160 } }}>
            <Typography
              variant="caption"
              sx={{ color: '#6c757d', fontSize: { xs: '0.65rem', md: '0.75rem' }, whiteSpace: 'nowrap' }}
            >
              Powered by Kamna Ventures
            </Typography>
          </Box>
        </Box>

        {/* Second Row: Menu Items */}
        <Toolbar sx={{ py: 1, flexWrap: 'wrap', gap: 1, alignItems: 'center', minHeight: '56px !important' }}>
          {/* Navigation Links - Starting from left */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flexGrow: 1 }}>
            {/* Upload tab hidden - functionality moved to Training tab */}
            {/* <Button
              startIcon={<UploadIcon sx={{ color: getIconColor(isActive('/upload')) }} />}
              onClick={() => navigate('/upload')}
              variant={isActive('/upload') ? 'contained' : 'text'}
              color={isActive('/upload') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/upload') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Upload
            </Button> */}
            <Button
              startIcon={<TrainingIcon sx={{ color: getIconColor(isActive('/training')) }} />}
              onClick={() => navigate('/training')}
              variant={isActive('/training') ? 'contained' : 'text'}
              color={isActive('/training') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/training') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Training
            </Button>
            <Button
              startIcon={<FolderIcon sx={{ color: getIconColor(isActive('/known')) }} />}
              onClick={() => navigate('/known')}
              variant={isActive('/known') ? 'contained' : 'text'}
              color={isActive('/known') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/known') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Files
            </Button>
            <Button
              startIcon={<HistoryIcon sx={{ color: getIconColor(isActive('/history')) }} />}
              onClick={() => navigate('/history')}
              variant={isActive('/history') ? 'contained' : 'text'}
              color={isActive('/history') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/history') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Extractions
            </Button>
            <Button
              startIcon={<PartsIcon sx={{ color: getIconColor(isActive('/parts')) }} />}
              onClick={() => navigate('/parts')}
              variant={isActive('/parts') ? 'contained' : 'text'}
              color={isActive('/parts') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/parts') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Parts
            </Button>
            {/* BOM tab hidden per user request */}
            {/* <Button
              startIcon={<DescriptionIcon sx={{ color: getIconColor(isActive('/bom')) }} />}
              onClick={() => navigate('/bom')}
              variant={isActive('/bom') ? 'contained' : 'text'}
              color={isActive('/bom') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/bom') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              BOM
            </Button> */}
            <Button
              startIcon={<LinkIcon sx={{ color: getIconColor(isActive('/reconciliation')) }} />}
              onClick={() => navigate('/reconciliation')}
              variant={isActive('/reconciliation') ? 'contained' : 'text'}
              color={isActive('/reconciliation') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/reconciliation') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Reconciliation
            </Button>
            <Button
              startIcon={<EstimatorIcon sx={{ color: getIconColor(isActive('/estimator')) }} />}
              onClick={() => navigate('/estimator')}
              variant={isActive('/estimator') ? 'contained' : 'text'}
              color={isActive('/estimator') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/estimator') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Estimator
            </Button>
            <Button
              startIcon={<AdminIcon sx={{ color: getIconColor(isActive('/admin')) }} />}
              onClick={() => navigate('/admin')}
              variant={isActive('/admin') ? 'contained' : 'text'}
              color={isActive('/admin') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/admin') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Admin
            </Button>
            <Button
              startIcon={<TeamsIcon sx={{ color: getIconColor(isActive('/teams')) }} />}
              onClick={() => navigate('/teams')}
              variant={isActive('/teams') ? 'contained' : 'text'}
              color={isActive('/teams') ? 'primary' : 'inherit'}
              sx={{
                color: isActive('/teams') ? 'inherit' : theme.palette.text.primary,
                fontWeight: currentTheme === 'ppcanda' ? 700 : 500,
              }}
            >
              Teams
            </Button>
          </Box>

          {/* User Profile Menu (right side of second row) */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <IconButton 
              onClick={handleMenuOpen} 
              size="small"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: '#ffc107',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  border: 'none'
                }}
              >
                {user?.username[0].toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Profile Menu (outside AppBar) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.full_name || user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {user?.email}
          </Typography>
          {user?.roles && user.roles.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {user.roles.map((role) => (
                <Typography 
                  key={role.id} 
                  variant="caption" 
                  sx={{ 
                    display: 'inline-block',
                    mr: 0.5,
                    px: 0.5,
                    py: 0.25,
                    bgcolor: '#f0f0f0',
                    borderRadius: 0.5,
                    fontSize: '0.65rem'
                  }}
                >
                  {role.display_name}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        <Divider />
        <MenuItem 
          onClick={() => {
            handleMenuClose();
            navigate('/settings');
          }}
        >
          <SettingsIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 1.5, fontSize: '1.2rem' }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 4, flexGrow: 1 }}>
        {children}
      </Container>
    </Box>
  );
};

export default Layout;

